import React, { useCallback, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import VPUnlockModal from './VPUnlockModal';
import VPSelectionModal from './VPSelectionModal';
import { getMySubscriptionRules } from '../redux/user/actions';
import { getUserOwnedVPs } from '../redux/valuePropositions/actions';
import { isUserAuthenticated, getLoggedInUser } from '../helpers/authUtils';
import { getVPLimit } from '../helpers/plan';

const SESSION_KEY_CAP_UP = 'vpCapUpPromptHandled';
const SESSION_KEY_CAP_DOWN = 'vpCapDownPromptHandled';

const pick = (obj, name) => {
  if (!obj) return undefined;
  const camel = name.charAt(0).toLowerCase() + name.slice(1);
  const pascal = name.charAt(0).toUpperCase() + name.slice(1);
  return Object.prototype.hasOwnProperty.call(obj, camel) ? obj[camel] : obj[pascal];
};

const getIsLimited = (vp) => !!(pick(vp, 'IsLimited'));

const readSession = (key) => {
  try {
    return sessionStorage.getItem(key) === '1';
  } catch (e) {
    return false;
  }
};

const writeSession = (key) => {
  try {
    sessionStorage.setItem(key, '1');
  } catch (e) {
    /* ignore */
  }
};

/**
 * After login, align the user with current plan VP limits when subscription policy changes:
 * - Cap-down (non-limited VPs > limit): VPSelectionModal — pick which VPs to keep
 * - Cap-up (active < limit + limited VPs): VPUnlockModal — pick which limited VPs to unlock
 *
 * Does not alter Subscription.js / home upgrade or downgrade checkout flows.
 */
const VpPolicyAlignmentPrompt = ({
  authUser,
  subscriptionRules,
  subscriptionRulesLoading,
  subscriptionRulesByPlan,
  userOwnedVPs,
  userOwnedVPsLoading,
  getMySubscriptionRules: loadMyRules,
  getUserOwnedVPs: loadOwnedVPs,
}) => {
  const [showUnlock, setShowUnlock] = useState(false);
  const [showCapDown, setShowCapDown] = useState(false);
  const [planId, setPlanId] = useState(null);
  const evaluatedRef = useRef(false);
  const requestedOwnedRef = useRef(false);
  const requestedRulesRef = useRef(false);

  const markCapUpHandled = useCallback(() => {
    writeSession(SESSION_KEY_CAP_UP);
    evaluatedRef.current = true;
    setShowUnlock(false);
  }, []);

  const markCapDownHandled = useCallback(() => {
    writeSession(SESSION_KEY_CAP_DOWN);
    evaluatedRef.current = true;
    setShowCapDown(false);
  }, []);

  // Re-run when auth changes (login/logout). Previous logic only watched rules and missed login.
  useEffect(() => {
    const authenticated = isUserAuthenticated() && !!(authUser || getLoggedInUser());

    if (!authenticated) {
      evaluatedRef.current = false;
      requestedOwnedRef.current = false;
      requestedRulesRef.current = false;
      setShowUnlock(false);
      setShowCapDown(false);
      setPlanId(null);
      return;
    }

    evaluatedRef.current = false;
    requestedOwnedRef.current = false;
    requestedRulesRef.current = false;

    if (!subscriptionRulesLoading) {
      requestedRulesRef.current = true;
      loadMyRules();
    }
  }, [authUser]); // eslint-disable-line react-hooks/exhaustive-deps -- intentionally only on auth change

  useEffect(() => {
    const authenticated = isUserAuthenticated() && !!(authUser || getLoggedInUser());
    if (!authenticated || evaluatedRef.current || showUnlock || showCapDown) return;
    if (subscriptionRulesLoading || !subscriptionRules) return;
    if (!subscriptionRulesByPlan) return;

    const rule = pick(subscriptionRules, 'Rule') || {};
    const plan =
      pick(rule, 'PlanId') ??
      authUser?.currentPlan ??
      authUser?.CurrentPlan ??
      getLoggedInUser()?.currentPlan ??
      getLoggedInUser()?.CurrentPlan;
    const limitFromRules = getVPLimit(subscriptionRulesByPlan, plan);
    const limitFromMy =
      pick(rule, 'ValuePropositionLimit') ??
      pick(subscriptionRules, 'ValuePropositionLimit');
    const limit = limitFromRules || limitFromMy || 0;
    const apiActiveCount = Number(
      pick(subscriptionRules, 'CurrentVPCount') ??
        pick(subscriptionRules, 'currentVPCount') ??
        0
    );

    if (!limit || plan == null) return;

    setPlanId(Number(plan));

    // Need owned VPs for accurate non-limited count (modal list) and unlock detection.
    if (!requestedOwnedRef.current && !userOwnedVPsLoading && userOwnedVPs == null) {
      requestedOwnedRef.current = true;
      loadOwnedVPs();
      return;
    }
    if (userOwnedVPsLoading || userOwnedVPs == null) return;

    const nonLimitedOwned = (userOwnedVPs || []).filter((vp) => !getIsLimited(vp)).length;
    const limitedCount = (userOwnedVPs || []).filter(getIsLimited).length;
    // Prefer the higher of API vs list so we don't miss over-cap users.
    const activeCount = Math.max(apiActiveCount, nonLimitedOwned);

    // Cap-down: more active / non-limited VPs than the current policy allows
    if (activeCount > limit) {
      if (readSession(SESSION_KEY_CAP_DOWN)) {
        evaluatedRef.current = true;
        return;
      }
      setShowCapDown(true);
      evaluatedRef.current = true;
      return;
    }

    // No spare slots → nothing to unlock
    if (activeCount >= limit) {
      evaluatedRef.current = true;
      return;
    }

    // Cap-up: spare slots — only if not dismissed this session
    if (readSession(SESSION_KEY_CAP_UP)) {
      evaluatedRef.current = true;
      return;
    }

    if (limitedCount > 0) {
      setShowUnlock(true);
    }
    evaluatedRef.current = true;
  }, [
    authUser,
    subscriptionRules,
    subscriptionRulesLoading,
    subscriptionRulesByPlan,
    userOwnedVPs,
    userOwnedVPsLoading,
    showUnlock,
    showCapDown,
    loadOwnedVPs,
  ]);

  if (planId == null) {
    return null;
  }

  return (
    <>
      {showCapDown && (
        <VPSelectionModal
          isOpen={showCapDown}
          toggleModal={markCapDownHandled}
          onConfirm={markCapDownHandled}
          newPlanId={planId}
          currentPlan={planId}
          isUpgrade={false}
          isPolicyCapDown
        />
      )}
      {showUnlock && (
        <VPUnlockModal
          isOpen={showUnlock}
          planId={planId}
          toggleModal={markCapUpHandled}
          onConfirm={markCapUpHandled}
        />
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  authUser: state.Auth?.user,
  subscriptionRules: state.User?.subscriptionRules,
  subscriptionRulesLoading: state.User?.subscriptionRulesLoading,
  subscriptionRulesByPlan: state.User?.subscriptionRulesByPlan,
  userOwnedVPs: state.ValueProposition?.userOwnedVPs,
  userOwnedVPsLoading: state.ValueProposition?.userOwnedVPsLoading,
});

export default connect(mapStateToProps, {
  getMySubscriptionRules,
  getUserOwnedVPs,
})(VpPolicyAlignmentPrompt);
